import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {UserMessageProps} from './user-message.types';
import styles from './user-message.module.scss';
import MarkdownRenderer from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer';
import {renderUserChatMarkdownHtml} from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer.library';
import type {ChatMessageDesignPart, ChatMessagePart, ChatMessageUserDocumentPart, ChatMessageUserImagePart, ImagePart} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType, isDocumentPart, TextPartPurpose} from '@Components/ai-chat-window/ai-chat-window.types';
import {UserAttachment} from './components/user-attachment';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {EditMessageBox} from '@Components/ai-chat-window/components/edit-message-box/edit-message-box';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {DocumentChip} from '@Components/document-chip/document-chip';
import {AttachmentRemoveButton} from '@Components/ai-prompt-box/components/attachment-remove-button/attachment-remove-button';
import {isImagePart} from '@Components/ai-chat-window/components/user-message-row/components/user-attachment/user-attachment.types';
import {BrandKitPreference} from './components/brand-kit-preference/brand-kit-preference';
import {RequestPreferenceCard} from './components/request-preference-card/request-preference-card';
import {getSeparatedStringFromCamelCase} from '@Utils/string.util';
import {AssistantMessageImagePartAnimationCreations} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations';
import {attachmentKeyOf, getAttachmentVideoSubject, isVideoAttachmentPart} from '@Components/ai-chat-window/lib/attachment-video-key.library';

function getMessageText(parts: ChatMessagePart[]): string {
  const textParts = parts.filter((p) => p.type === ChatMessagePartType.TEXT);
  return textParts.map((p) => p.text ?? '').join('\n');
}

function UserMessageRowInner({messageKey, ...props}: UserMessageProps): ReactElement {
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [removedDocumentHashedIds, setRemovedDocumentHashedIds] = useState<string[]>([]);
  const {state, actions} = useAiChat();
  const editingId = props.message.messageId ?? messageKey;
  const isEditing = props.chatId ? state.editingUserMessageIdMap[props.chatId] === editingId : false;
  const {actions: promptBoxActions} = useAiPromptBox(state.promptBoxId);
  const parts = props.message.parts ?? [];
  const attachmentParts = parts.filter(isImagePart);
  const documentParts = parts.filter(isDocumentPart);
  const hasAttachments = attachmentParts.length > 0 || documentParts.length > 0;
  const textParts = parts.filter((part) => part.type === ChatMessagePartType.TEXT);

  useEffect(() => {
    if (isEditing) {
      setEditValue(getMessageText(props.message.parts ?? []));
      setRemovedDocumentHashedIds([]);
    }
  }, [isEditing, props.message.parts]);

  const handleAttachmentImageClick = (uniqueKey: string): void => {
    props.onImageClick(props.message.messageId, uniqueKey);
  };

  const getDocumentChip = (part: ChatMessageUserDocumentPart): ReactElement => {
    const chip = <DocumentChip key={part.documentHashedId} filename={part.filename} documentType={part.documentType} onClick={(): void => props.onDocumentClick?.(part)} />;
    if (!isEditing) {
      return chip;
    }

    return (
      <div key={part.documentHashedId} className={styles.removableDocument}>
        {chip}
        <AttachmentRemoveButton
          onClick={(): void => {
            setRemovedDocumentHashedIds((removed) => [...removed, part.documentHashedId]);
          }}
        />
      </div>
    );
  };

  const getAttachmentParts = (): ReactElement => {
    const keptDocumentParts = isEditing ? documentParts.filter((part) => !removedDocumentHashedIds.includes(part.documentHashedId)) : documentParts;

    return (
      <>
        {keptDocumentParts.map(getDocumentChip)}
        {attachmentParts.map(getAttachment)}
      </>
    );
  };

  const hasAttachmentVideo = (part: ChatMessageUserImagePart | ChatMessageDesignPart, attachmentVideoKey: string): boolean => {
    const hasSavedVideo = (part.animations?.length ?? 0) > 0;

    return hasSavedVideo || state.getAnimationCreationState(props.chatId, attachmentVideoKey).isVisible;
  };

  const getAttachmentVideoStrip = (part: ChatMessageUserImagePart | ChatMessageDesignPart): ReactElement => {
    return (
      <div className={styles.attachmentVideo}>
        <AssistantMessageImagePartAnimationCreations
          chatId={props.chatId}
          videoSubject={getAttachmentVideoSubject(part)}
          animations={part.animations}
        />
      </div>
    );
  };

  const getAttachment = (part: ImagePart, index: number): ReactElement => {
    const partKey = `user-part-${index}`;
    const attachment = (
      <UserAttachment key={partKey} part={part} partKey={partKey} onImageClick={handleAttachmentImageClick} onReattach={props.onReattach} isMobile={props.isMobile} />
    );
    if (!isVideoAttachmentPart(part)) {
      return attachment;
    }

    const attachmentVideoKey = attachmentKeyOf(part);
    if (!hasAttachmentVideo(part, attachmentVideoKey)) {
      return attachment;
    }

    return (
      <div key={partKey} data-testid={AiChatTestId.USER_ATTACHMENT_WITH_VIDEO} className={styles.attachmentWithVideo}>
        {attachment}
        {getAttachmentVideoStrip(part)}
      </div>
    );
  };

  const getAttachments = (): ReactElement | null => {
    if (!hasAttachments) {
      return null;
    }

    return <div className={`flex-justify-end spacing-m-b-1 flexbox flex-align-items-center gap-2 ${styles.attachments}`}>{getAttachmentParts()}</div>;
  };

  const handleCopyMessage = async (): Promise<void> => {
    const messageText = getMessageText(props.message.parts ?? []);
    const copyTooltipDurationMs = 3000;

    try {
      await navigator.clipboard.writeText(messageText);
      setShowCopiedTooltip(true);
      setTimeout(() => {
        setShowCopiedTooltip(false);
      }, copyTooltipDurationMs);
    } catch (error) {
      console.error('Failed to copy message to clipboard', error);
    }
  };

  const getEditButton = (): ReactElement | null => {
    if (props.message.deletedAt) {
      return null;
    }

    return (
      <OverlayTrigger
        overlay={
          <Tooltip className="spacing-m-b-2 _rounded-arrow">
            {props.isMessageStreaming ? window.i18next.t('pmwjs_cant_edit_while_streaming') : window.i18next.t('pmwjs_edit')}
          </Tooltip>
        }
        trigger={['hover', 'focus']}
        placement="top"
        key={`tooltip-edit-message`}
      >
        <span>
          <Icon
            data-testid={AiChatTestId.USER_MESSAGE_EDIT}
            type={IconType.GHOST}
            shape={IconShape.CIRCLE}
            icon="icon-pencil"
            size={IconSize.SIZE_ICON_16}
            isDisabled={props.isMessageStreaming}
            onClick={(): void => {
              if (!props.isMessageStreaming) {
                promptBoxActions.update({promptBoxState: PromptBoxState.IDLE});
                actions.setEditingUserMessageId(editingId, props.chatId);
              }
            }}
          />
        </span>
      </OverlayTrigger>
    );
  };

  const getHandleCopyButton = (): ReactElement => {
    return (
      <OverlayTrigger
        overlay={<Tooltip className="spacing-m-b-2 _rounded-arrow">{showCopiedTooltip ? window.i18next.t('pmwjs_copied') : window.i18next.t('pmwjs_copy')}</Tooltip>}
        trigger={['hover', 'focus']}
        placement="top"
        key={`tooltip-copy-message`}
      >
        <Icon
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          icon="icon-copy"
          size={IconSize.SIZE_ICON_16}
          onClick={(): void => {
            void handleCopyMessage();
          }}
        />
      </OverlayTrigger>
    );
  };

  const getActionIcons = (): ReactElement | null => {
    return (
      <div
        className={`flex-justify-end flex-h-row flex-align-items-center ${!props.isMobile ? 'gap-1' : 'gap-2'} ${styles.actionIconsContainer} ${!props.isMobile ? styles.actionIconsDesktop : ''}`}
      >
        {getEditButton()}
        {getHandleCopyButton()}
      </div>
    );
  };

  const getEditMessageBox = (): ReactElement => {
    return (
      <div className={`flex-v-row ${styles.userMessage} ${styles.editMode} ${props.isMobile ? styles.userMessageSmall : ''}`}>
        {getAttachments()}
        <EditMessageBox
          value={editValue}
          onChange={setEditValue}
          onCancel={(): void => {
            actions.setEditingUserMessageId(null, props.chatId);
          }}
          onSend={(): void => {
            props.onEditSend?.(props.message.messageId ?? messageKey, editValue, removedDocumentHashedIds);
          }}
          isLoading={props.editSendInProgress}
          promptBoxId={state.promptBoxId}
        />
      </div>
    );
  };

  const getSuperUserPreferences = (): ReactElement | null => {
    const requestPreferences = props.message.requestPreferences;

    if (!state.isSuperUser || state.hideGenerationContext || !requestPreferences) {
      return null;
    }

    const {brandKitId, ...otherPreferences} = requestPreferences;
    const entries = Object.entries(otherPreferences).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0);

    if (entries.length === 0 && !brandKitId) {
      return null;
    }

    const getBrandKitPreference = (): ReactElement | null => {
      if (!brandKitId) {
        return null;
      }
      return <BrandKitPreference brandKitId={brandKitId} />;
    };

    return (
      <div className={styles.superUserPreferences}>
        {entries.map(([preferenceKey, value]) => (
          <RequestPreferenceCard key={preferenceKey} label={getSeparatedStringFromCamelCase(preferenceKey)} value={value} />
        ))}
        {getBrandKitPreference()}
      </div>
    );
  };

  const getMessageContent = (): ReactElement => {
    return (
      <div className={`flex-v-row ${styles.userMessage} ${props.isMobile ? styles.userMessageSmall : ''} ${!props.isMobile ? styles.userMessageDesktop : ''}`}>
        {getAttachments()}
        {textParts.map((part, index) => (
          <div
            key={`user-text-part-${index}`}
            className={`${props.bubbleClassNames} ${styles.userText} ${part.purpose === TextPartPurpose.CHANGE_STYLE ? styles.changeStyleText : ''}`}
          >
            <MarkdownRenderer content={part.text ?? ''} toHtml={renderUserChatMarkdownHtml} />
          </div>
        ))}
        {getSuperUserPreferences()}
        {getActionIcons()}
      </div>
    );
  };

  const renderContent = (): ReactElement => {
    if (isEditing) {
      return getEditMessageBox();
    } else {
      return getMessageContent();
    }
  };

  return renderContent();
}

export const UserMessageRow = React.memo(UserMessageRowInner);
